import {
    FilterObject,
    GeographyType,
    InputFilterObject,
} from "@biggeo/bg-server-lib/studio";
import {
    FilterItem as ProtoFilterItem,
    RequestOptions,
    OrderBy,
} from "../../../velocity/aurora/filter";
import { DataReq } from "../../../velocity/aurora/map";

import { DataRspRow } from "../../utils/type";
import { SpatialReq } from "../../../velocity/aurora/spatial";
import { v4 as uuidV4 } from "uuid";
import { Polygon } from "../../../velocity/bgsearch/bgsearch_types";
import {
    matchGeographyColumnValue,
    toNonReadonlyArray,
} from "@biggeo/bg-utils";
import { match } from "ts-pattern";
import { GeospatialSelection } from "../context/types";
import {
    convertDatabaseTypeProto,
    convertFilterTypeProto,
    convertLogicOperatorProto,
    convertOperatorProto,
    geospatialSelectionIsMultipolygon,
    geospatialSelectionIsViewport,
} from "../context/utils/map-utils";

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const getNestedValue = (row: DataRspRow, path: string): any => {
    return path.split(".").reduce((acc, key) => acc[key], row);
};

export const getLongLat = (input: {
    geographyColumn: string;
    geographyType?: GeographyType | null;
    row: DataRspRow;
}): {
    longitude: number;
    latitude: number;
} => {
    const { geographyColumn, row, geographyType } = input;

    const { lonColumnName: longitudeColumn, latColumnName: latitudeColumn } =
        matchGeographyColumnValue(geographyColumn);

    const noPoints = {
        longitude: 0,
        latitude: 0,
    };

    return match(geographyType)
        .with(GeographyType.LonLat, () => ({
            longitude: longitudeColumn ? row[longitudeColumn] : 0,
            latitude: latitudeColumn ? row[latitudeColumn] : 0,
        }))

        .with(GeographyType.JSON, () => {
            if (!longitudeColumn || !latitudeColumn) {
                return noPoints;
            }

            // This is to fix issues for datasets where the actual column has "."
            // e.g. UFO dataset that has column: Locations.Coordinates.Longitude
            return {
                longitude: longitudeColumn.includes(".")
                    ? getNestedValue(row, longitudeColumn)
                    : row[longitudeColumn],
                latitude: latitudeColumn.includes(".")
                    ? getNestedValue(row, latitudeColumn)
                    : row[latitudeColumn],
            };
        })
        .with(GeographyType.GeoJSON, () => {
            const coordinates = JSON.parse(row[geographyColumn]).coordinates;

            const lon = coordinates?.[0];
            const lat = coordinates?.[1];

            return {
                longitude: lon || 0,
                latitude: lat || 0,
            };
        })
        .otherwise(() => noPoints);
};

export const getFilteredDataInput = (input: {
    geospatialSelection?: GeospatialSelection;
    filter: FilterObject;
    fkDataSourceId: string;
    fkOrganizationId: string;
    limit: number;
    offset: number;
    orderBy?: OrderBy;
}): DataReq => {
    const {
        geospatialSelection,
        filter,
        fkOrganizationId,
        fkDataSourceId,
        limit,
        offset,
        orderBy,
    } = input;

    const multipolygonInput =
        geospatialSelection &&
        geospatialSelectionIsMultipolygon(geospatialSelection)
            ? geospatialSelection.multipolygon
            : undefined;

    const viewportInput =
        geospatialSelection &&
        geospatialSelectionIsViewport(geospatialSelection)
            ? geospatialSelection.viewport
            : undefined;

    return {
        filters: {
            databaseId: fkDataSourceId,
            organizationId: fkOrganizationId,
            filters: filter.filters.map(
                (filterItem): ProtoFilterItem => ({
                    column: filterItem.column,
                    operator: convertOperatorProto(filterItem.operator),
                    type: convertFilterTypeProto(filterItem.type),
                    data: {
                        stringData: filterItem.data.stringData ?? undefined,
                        booleanData: filterItem.data.booleanData ?? undefined,
                        dateData: filterItem.data.dateData ?? undefined,
                        numberData: filterItem.data.numberData ?? undefined,
                    },
                    id: filterItem.id ?? undefined,
                })
            ),
            idColumn: "",
            logicOperator: convertLogicOperatorProto(filter.logicOperator),
            collection: filter.collection,
            databaseType: convertDatabaseTypeProto(filter.databaseType),
        },
        multipolygon: multipolygonInput
            ? {
                  polygons: multipolygonInput.map((inputPolygon) => ({
                      inners: inputPolygon.inners,
                      outer: inputPolygon.outer,
                  })) as Polygon[],
              }
            : undefined,
        viewport:
            viewportInput && !multipolygonInput
                ? {
                      bottomRight: {
                          latitude: viewportInput.latBounds.min,
                          longitude: viewportInput.lngBounds.max,
                      },
                      topLeft: {
                          latitude: viewportInput.latBounds.max,
                          longitude: viewportInput.lngBounds.min,
                      },
                  }
                : undefined,
        limitOffset: {
            limit,
            offset,
        },
        order: orderBy,
    };
};

export const getSpatialReq = (input: {
    geospatialSelection?: GeospatialSelection;
    filters: {
        fkDataSourceId: string;
        fkOrganizationId: string;
        tableId: string;
        filterObject: FilterObject;
    }[];
    limit?: number;
    offset?: number;
    channel: string;
}): SpatialReq => {
    const { geospatialSelection, filters, limit, offset, channel } = input;

    const multipolygonInput =
        geospatialSelection &&
        geospatialSelectionIsMultipolygon(geospatialSelection)
            ? geospatialSelection.multipolygon
            : undefined;

    const viewportInput =
        geospatialSelection &&
        geospatialSelectionIsViewport(geospatialSelection)
            ? geospatialSelection.viewport
            : undefined;

    return {
        withTable: false,
        requestId: uuidV4(),
        dateTime: Date.now().toString(),
        channel: channel,
        multiFilters: filters.map((filter) => {
            const options: RequestOptions | undefined = filter.filterObject
                .options
                ? {
                      pointMaxGeomsPerCell:
                          filter.filterObject.options.pointMaxGeomsPerCell ||
                          undefined,
                      pointResolutionOffset:
                          filter.filterObject.options.pointResolutionOffset ||
                          undefined,
                      polygonMaxGeomsPerCell:
                          filter.filterObject.options.polygonMaxGeomsPerCell ||
                          undefined,
                      polygonResolutionOffset:
                          filter.filterObject.options.polygonResolutionOffset ||
                          undefined,
                      geometriesChunkSize:
                          filter.filterObject.options.geometriesChunkSize ||
                          undefined,
                      aggregatesChunkSize:
                          filter.filterObject.options.aggregatesChunkSize ||
                          undefined,
                      maxAdditionalRefines:
                          filter.filterObject.options.maxAdditionalRefines ||
                          undefined,
                      levelSets:
                          toNonReadonlyArray(
                              filter.filterObject.options.levelSets
                          ) || [],
                  }
                : undefined;

            const { fkDataSourceId, fkOrganizationId, tableId } = filter;

            return {
                databaseId: fkDataSourceId,
                organizationId: fkOrganizationId,
                idColumn: tableId || "BG_ID",
                filters: filter.filterObject.filters.map(
                    (filterItem): ProtoFilterItem => ({
                        column: filterItem.column,
                        operator: convertOperatorProto(filterItem.operator),
                        type: convertFilterTypeProto(filterItem.type),
                        data: {
                            stringData: filterItem.data.stringData ?? undefined,
                            booleanData:
                                filterItem.data.booleanData ?? undefined,
                            dateData: filterItem.data.dateData ?? undefined,
                            numberData: filterItem.data.numberData ?? undefined,
                        },
                        id: filterItem.id ?? undefined,
                    })
                ),
                options: options,
                logicOperator: convertLogicOperatorProto(
                    filter.filterObject.logicOperator
                ),
                collection: filter.filterObject.collection,
                databaseType: convertDatabaseTypeProto(
                    filter.filterObject.databaseType
                ),
                limitOffset:
                    limit && offset
                        ? {
                              limit,
                              offset,
                          }
                        : undefined,
            };
        }),
        multipolygon: multipolygonInput
            ? {
                  polygons: multipolygonInput.map((inputPolygon) => ({
                      inners: inputPolygon.inners,
                      outer: inputPolygon.outer,
                  })) as Polygon[],
              }
            : undefined,
        viewport:
            viewportInput && !multipolygonInput
                ? {
                      bottomRight: {
                          latitude: viewportInput.latBounds.min,
                          longitude: viewportInput.lngBounds.max,
                      },
                      topLeft: {
                          latitude: viewportInput.latBounds.max,
                          longitude: viewportInput.lngBounds.min,
                      },
                  }
                : undefined,
    };
};

/**
 * Converts a string name to a parquet filename format
 * Trims leading/trailing spaces, replaces internal spaces with underscores,
 * and adds .parquet extension
 *
 * @param name - The input string to be converted
 * @returns The formatted parquet filename
 */
export function convertToParquetFileName(name: string): string {
    const trimmedName = name.trim();

    const nameWithUnderscores = trimmedName.replace(/ /g, "_");

    return `${nameWithUnderscores}.parquet`;
}

export function convertFilterObjectToInputType(
    filterObject: FilterObject
): InputFilterObject {
    return {
        databaseId: filterObject.databaseId,
        filters: filterObject.filters.map((filterItem) => ({
            id: filterItem.id,
            type: filterItem.type,
            column: filterItem.column,
            data: {
                stringData: filterItem.data.stringData,
                numberData: filterItem.data.numberData,
                dateData: filterItem.data.dateData,
                booleanData: filterItem.data.booleanData,
            },
            operator: filterItem.operator,
        })),
        logicOperator: filterObject.logicOperator,
        collection: filterObject.collection,
        databaseType: filterObject.databaseType,
        options: filterObject.options
            ? {
                  pointMaxGeomsPerCell:
                      filterObject.options.pointMaxGeomsPerCell,
                  pointResolutionOffset:
                      filterObject.options.pointResolutionOffset,
                  polygonMaxGeomsPerCell:
                      filterObject.options.polygonMaxGeomsPerCell,
                  polygonResolutionOffset:
                      filterObject.options.polygonResolutionOffset,
                  geometriesChunkSize: filterObject.options.geometriesChunkSize,
                  aggregatesChunkSize: filterObject.options.aggregatesChunkSize,
                  maxAdditionalRefines:
                      filterObject.options.maxAdditionalRefines,
                  levelSets: filterObject.options.levelSets,
              }
            : undefined,
    };
}
